import {
  Context,
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useState
} from 'react';
import { NotificationInterface } from '@interfaces/Notification.interface';
import api from '@api-client/client';
import { useStore } from '@hooks/useStore';

interface NotificationContextType {
  count: number;
  inbox: NotificationInterface[];
  working: string | false;
  workingAll: boolean;
  fetching: boolean;
  currentPage: number;
  hasMore: boolean;
  setHasMore: Dispatch<SetStateAction<boolean>>;

  fetchCount: () => void;
  fetchData: (page?: number) => void;
  markAllAsRead: () => void;
  markAsRead: (id: string, callback?: () => void) => void;
  handleDeleteNotification: (id: string) => void;
  markForDelete: string | null;
  setMarkForDelete: Dispatch<SetStateAction<string | null>>;
}

const _NotificationContext = createContext<object>({});

export function NotificationContext(): Context<NotificationContextType> {
  return _NotificationContext as Context<NotificationContextType>;
}

export function useNotificationContext() {
  return useContext<NotificationContextType>(
    NotificationContext()
  ) as NotificationContextType;
}

export function NotificationContextProvider({
  children
}: {
  children: ReactNode;
}) {
  const [count, setCount] = useState(0);
  const [inbox, setInbox] = useState<NotificationInterface[]>([]);
  const [working, setWorking] = useState<string | false>(false);
  const [workingAll, setWorkingAll] = useState<boolean>(false);
  const [fetching, setFetching] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 4;
  const [hasMore, setHasMore] = useState(true);
  const [markForDelete, setMarkForDelete] = useState<string | null>(null);

  const { toast } = useStore();

  /**
   * Fetch count
   */
  const fetchCount = () => {
    api.notification.count().then((data) => {
      setCount(data.new);
    });
  };

  /**
   * Fetch data
   */
  const fetchData = (page = 1) => {
    if (fetching) return;
    setFetching(true);
    api.notification
      .list(page, perPage)
      .then((data) => {
        if (page === 1) {
          // If page is one then replace whole inbox with items
          setInbox(data.items);
        } else {
          // If page is greater than one then append the items to existing ones
          setInbox([...inbox].concat(data.items));
        }
        setCurrentPage(page);
        // Check if no items are returned and set the hasMore state flag
        if (!data.items || data.items.length === 0) {
          setHasMore(false);
        } else {
          // If items returned are less than the perPage then mark hasMore as false
          setHasMore(data.items.length === perPage);
        }
      })
      .finally(() => {
        setFetching(false);
      });
  };

  /**
   * Mark all items as read
   */
  const markAllAsRead = () => {
    setWorkingAll(true);
    api.notification
      .markAllRead()
      .then(() => {
        setCount(0);
        // Mark all items in inbox as read
        setInbox(
          inbox.map((i) => {
            i.read_at = i.read_at || new Date().toString();
            return i;
          })
        );
      })
      .catch((e) => {
        console.error(e);
        toast.error('Problem marking notifications as read.');
      })
      .finally(() => {
        setWorkingAll(false);
      });
  };

  /**
   * Mark an item as read
   */
  const markAsRead = (id: string, callback?: () => void) => {
    setWorking(id);
    api.notification
      .markRead(id)
      .then(() => {
        const newInbox = [...inbox];
        // Mark item in inbox as read
        const index = newInbox.findIndex((item) => item.id === id);
        if (index) {
          newInbox[index].read_at = new Date().toString();
        }
        setInbox(newInbox);
        fetchCount();
        if (callback) {
          callback();
        }
      })
      .catch((e) => {
        console.error(e);
        toast.error('Problem marking notification as read.');
      })
      .finally(() => {
        setWorking(false);
      });
  };

  /**
   * Delete item by id
   */
  const handleDeleteNotification = (id: string) => {
    setWorking(id);
    api.notification
      .delete(id)
      .then(() => {
        // Remove item from inbox
        const newInbox = inbox.filter((item) => item.id !== id);
        setInbox(newInbox);
        setMarkForDelete(null);
        fetchCount();
        toast.success('Notification deleted.');
      })
      .catch((e) => {
        console.error(e);
        toast.error('Problem deleting notification.');
      })
      .finally(() => {
        setWorking(false);
      });
  };

  const Ctx = NotificationContext();
  return (
    <Ctx.Provider
      value={{
        count,
        inbox,
        working,
        workingAll,
        fetching,
        currentPage,
        hasMore,
        setHasMore,
        fetchCount,
        fetchData,
        markAllAsRead,
        markAsRead,
        handleDeleteNotification,
        markForDelete,
        setMarkForDelete
      }}
    >
      {children}
    </Ctx.Provider>
  );
}
